import { garantirPerto } from './FaixaOficio';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ApiError, type FletchMenu } from '../api/types';
import { playArcos } from '../audio/mixer';
import { prefetchIcons } from '../assets/items';
import { idiomaAtual, numero, t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { CraftBar } from './CraftBar';
import { ItemIcon } from './ItemIcon';
import { Linha } from './kit';

interface Props {
  active: boolean;
  tick: number;
  onResultado(ev: { evento: 'entalhou' | 'empenou'; nome?: string; n?: number }): void;
}

/** Cobre arcos.* no validar-chaves: os códigos chegam pelo `code` HTTP. */
const chaveArcos = (x: string) => t(`arcos.${x}`);

export function FletchGump({ active, tick, onResultado }: Props) {
  const [menu, setMenu] = useState<FletchMenu | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preparo, setPreparo] = useState<{ ate: number; ms: number } | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current != null) window.clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (!active) return;
    let live = true;
    void api.fletchMenu()
      .then(next => {
        if (!live) return;
        setMenu(next);
        setError(null);
        prefetchIcons([
          ...next.receitas.map(r => r.defId),
          ...next.receitas.flatMap(r => r.insumos.map(i => i.defId)),
          'fletcher-tools'
        ]);
      })
      .catch(err => {
        if (!live) return;
        setError(err instanceof ApiError ? err.message : t('arcos.naoEntalhou'));
      });
    return () => { live = false; };
  }, [active, tick]);

  const entalhar = async (defId: string, rende: number) => {
    setBusy(true);
    setError(null);
    try {
      const perto = await garantirPerto('arcos');
      if (!perto.ok) { setBusy(false); setError(perto.motivo ?? null); return; }
      playArcos();
      const res = await api.carve(defId);
      const peca = res.pack.items.find(i => i.defId === defId);
      const nome = peca ? nomeDoItem(peca.defId, peca.name) : undefined;
      setPreparo({ ate: performance.now() + res.prontoEmMs, ms: res.prontoEmMs });
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setPreparo(null);
        setBusy(false);
        if (defId === 'arrow' || defId === 'bolt') onResultado({ evento: 'empenou', nome, n: rende });
        else onResultado({ evento: 'entalhou', nome });
      }, res.prontoEmMs);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError) {
        setError(err.message);
        const x = (err.code ?? '').startsWith('arcos.') ? err.code!.slice('arcos.'.length) : '';
        if (x) void chaveArcos(x);
      } else {
        setError(t('arcos.naoEntalhou'));
      }
    }
  };

  const semFerramenta = menu !== null && !menu.temFerramenta;

  return (
    <>
      {preparo && <CraftBar ateMs={preparo.ate} duracaoMs={preparo.ms} rotulo={t('arcos.entalhando')} />}
      {error && <p className="notice" role="alert">{error}</p>}
      {semFerramenta && <p className="notice" role="alert">{t('arcos.semFerramenta')}</p>}
      <p className="smith-skill alchemy-head">
        {t('arcos.suaPericia', { n: pericia(menu?.arcos ?? 0) })}
        <span>{t('arcos.ferramentas')} {menu?.temFerramenta ? '✔' : '-'}</span>
        <span>{t('arcos.materiais', { tora: numero(menu?.tora ?? 0), haste: numero(menu?.haste ?? 0), pena: numero(menu?.pena ?? 0) })}</span>
      </p>
      <ul className="pack-list smith-list fletch-list" aria-label={t('arcos.titulo')}>
        {!menu && <li className="empty">{t('comum.montando')}</li>}
        {menu?.receitas.map(linha => {
          const estado = estadoLinha(linha, semFerramenta);
          const nome = nomeDoItem(linha.defId, linha.name);
          const insumos = textoInsumos(linha);
          return (
            <li key={linha.defId} className={estado.travado ? 'is-lock' : undefined}>
              <Linha
                lead={<ItemIcon id={linha.defId} label={nome} size={36} eager />}
                trail={(
                  <button
                    type="button"
                    className="btn btn--tiny"
                    disabled={busy || estado.desabilitado}
                    title={estado.titulo}
                    onClick={() => void entalhar(linha.defId, linha.rende)}
                  >
                    {estado.rotulo}
                  </button>
                )}
              >
                {nome}
                <span className="smith-need">{t('arcos.custo', { insumos, skill: pericia(linha.skill) })}</span>
              </Linha>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function pericia(n: number): string {
  if (Number.isInteger(n)) return numero(n);
  return n.toLocaleString(idiomaAtual(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function textoInsumos(linha: FletchMenu['receitas'][number]): string {
  return linha.insumos
    .map(i => t('arcos.pronto', { n: i.qtd, material: nomeDoItem(i.defId, i.name) }))
    .join(' + ');
}

function estadoLinha(
  linha: FletchMenu['receitas'][number],
  semFerramenta: boolean
): { travado: boolean; desabilitado: boolean; rotulo: string; titulo?: string } {
  if (semFerramenta)
    return { travado: false, desabilitado: true, rotulo: t('arcos.entalhar'), titulo: t('arcos.semFerramenta') };
  if (!linha.podeSkill)
    return { travado: true, desabilitado: true, rotulo: `🔒 ${pericia(linha.skill)}`, titulo: t('arcos.periciaBaixa', { n: pericia(linha.skill) }) };
  const falta = linha.insumos.find(i => i.tenho < i.qtd);
  if (falta) {
    const material = nomeDoItem(falta.defId, falta.name);
    return { travado: false, desabilitado: true, rotulo: t('arcos.faltam', { n: falta.qtd - falta.tenho, material }) };
  }
  const pronto = textoInsumos(linha);
  return {
    travado: false,
    desabilitado: false,
    rotulo: linha.rende > 1 ? `${pronto} → ${numero(linha.rende)}` : pronto
  };
}
